"use client";

import { useState, useEffect } from "react";

interface HelpfulButtonProps {
  reviewId: string;
  initialLikesCount?: number;
  onLikesCountChange?: (count: number) => void;
  onHelpfulClick?: (action: string) => Promise<{ success: boolean; data?: any; error?: any }>;
}

const HelpfulButton = ({ reviewId, initialLikesCount = 0, onLikesCountChange, onHelpfulClick }: HelpfulButtonProps) => {
  const [isHelpful, setIsHelpful] = useState(false);
  const [likesCount, setLikesCount] = useState(initialLikesCount);

  useEffect(() => {
    const helpful = localStorage.getItem(`helpful_${reviewId}`) === 'true';
    setIsHelpful(helpful);
  }, [reviewId]);

  const handleHelpful = async () => {
    const previousState = isHelpful;
    const previousCount = likesCount;
    
    if (isHelpful) {
      setIsHelpful(false);
      const result = await onHelpfulClick?.("dislike");
      if (result?.success) {
        const newCount = Math.max(0, previousCount - 1);
        setLikesCount(newCount);
        onLikesCountChange?.(newCount);
        localStorage.removeItem(`helpful_${reviewId}`);
        removeFromPendingLikes(reviewId);
      } else {
        setIsHelpful(previousState);
      }
    } else {
      setIsHelpful(true);
      const result = await onHelpfulClick?.("like");
      if (result?.success) {
        const newCount = previousCount + 1;
        setLikesCount(newCount);
        onLikesCountChange?.(newCount);
        localStorage.setItem(`helpful_${reviewId}`, 'true');
        addToPendingLikes(reviewId);
      } else {
        setIsHelpful(previousState);
      }
    }
  };

  const addToPendingLikes = (reviewId: string) => {
    const pending = JSON.parse(localStorage.getItem('pendingLikes') || '[]');
    if (!pending.includes(reviewId)) {
      pending.push(reviewId);
      localStorage.setItem('pendingLikes', JSON.stringify(pending));
    }
  };

  const removeFromPendingLikes = (reviewId: string) => {
    const pending = JSON.parse(localStorage.getItem('pendingLikes') || '[]');
    const updated = pending.filter((id: string) => id !== reviewId);
    localStorage.setItem('pendingLikes', JSON.stringify(updated));
  };

  return (
    <div className="inline-flex items-center cursor-pointer" onClick={handleHelpful}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#clip0_6431_14318)">
          <path 
            d="M4.66732 14.6668H2.66732C2.3137 14.6668 1.97456 14.5264 1.72451 14.2763C1.47446 14.0263 1.33398 13.6871 1.33398 13.3335V8.66683C1.33398 8.31321 1.47446 7.97407 1.72451 7.72402C1.97456 7.47397 2.3137 7.3335 2.66732 7.3335H4.66732M9.33398 6.00016V3.3335C9.33398 2.80306 9.12327 2.29436 8.7482 1.91928C8.37313 1.54421 7.86442 1.3335 7.33398 1.3335L4.66732 7.3335V14.6668H12.1873C12.5089 14.6705 12.8209 14.5578 13.0659 14.3495C13.3109 14.1412 13.4724 13.8514 13.5207 13.5335L14.4407 7.5335C14.4697 7.3424 14.4568 7.14728 14.4029 6.96166C14.349 6.77604 14.2554 6.60436 14.1286 6.45851C14.0017 6.31265 13.8447 6.19612 13.6684 6.11697C13.492 6.03783 13.3006 5.99797 13.1073 6.00016H9.33398Z" 
            stroke={isHelpful ? "#4664DC" : "#4664DC"} 
            fill={isHelpful ? "#4664DC" : "none"}
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round" 
          />
        </g>
        <defs>
          <clipPath id="clip0_6431_14318">
            <rect width="16" height="16" fill="white" />
          </clipPath>
        </defs>
      </svg>
      <small className={`ml-[8px] underline ${isHelpful ? 'text-primary-400 font-semibold' : 'text-primary-400'} small`}>
        {likesCount}
      </small>
    </div>
  );
};

export default HelpfulButton;